import { get, isEmpty, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../../colors-v2';
import { Box } from '../../../../../Common/BoxV2';
import { Typography } from '../../../../../DesignSystemV2/Typography';
import { findBellowByType } from '../../../../../DolEditor/helpers/nodeValue';
import { SlateAutoSizeInput } from '../../../../../SlateAudoSizeInput/SlateAutoSizeInput';
import { CORRECT_ANSWER } from '../../../../plugins-common/CorrectAnswers/types';
import { PARAGRAPH_TOGGLE } from '../../../../plugins-common/ParagraphToggle/types';
import { useExerciseShortAnswerDoTestStore } from '../../store/exercise-short-answer-do-test.store';
import { useRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';

/* eslint-disable no-nested-ternary */
const ShortAnswerInputMain = styled.div`
  .result-missing {
    border-bottom: 1px solid ${colorsV2.gray40};
  }

  .result-correct {
    border-bottom: 1px solid ${colorsV2.green80};
  }

  .result-wrong {
    border-bottom: 1px solid ${colorsV2.red100};
  }
`;

export const ShortAnswerInput = observer((props: RenderElementProps) => {
  const { element, children } = props;
  const { id } = element;
  const store = useExerciseShortAnswerDoTestStore();
  const shortAnswerElement = useRootElement();

  const correctAnswerNodes = findBellowByType(shortAnswerElement, CORRECT_ANSWER).filter(
    (n) => n.forId === element.id
  );

  const correctAnswer: string = get(correctAnswerNodes, '0.correctAnswer', '');

  const correctAnswers = React.useMemo(() => {
    return correctAnswer?.includes('/')
      ? correctAnswer.split('/').map((st) => st.trim())
      : [correctAnswer.trim()];
  }, [correctAnswer]);

  const lowerCaseCorrectAnswers = correctAnswers.map((c) => c.toLowerCase());

  const userAnswer = store.userAnswers.get(id)?.userAnswer ?? '';
  const isMiss = isEmpty(userAnswer);
  const isCorrect = lowerCaseCorrectAnswers.includes(userAnswer.trim().toLowerCase());

  const paragraphToggle = findBellowByType(element, PARAGRAPH_TOGGLE);

  const isNotInsideSubject = isNil(paragraphToggle);

  const renderResult = React.useCallback(() => {
    if (isMiss) {
      return (
        <Typography.Text className="result-missing" variant="regular/20" color="green100">
          {correctAnswers[0]}
        </Typography.Text>
      );
    }
    if (isCorrect) {
      return (
        <Typography.Text className="result-correct" variant="regular/20" color="green100">
          {correctAnswers[0]}
        </Typography.Text>
      );
    }
    return (
      <>
        <Typography.Text className="result-wrong" variant="regular/20" color="red100">
          {userAnswer}
        </Typography.Text>
        <Typography.Text variant="regular/20" className="result-correct" color="green100" ml="8px">
          {correctAnswers[0]}
        </Typography.Text>
      </>
    );
  }, [correctAnswers, isCorrect, isMiss, userAnswer]);

  return (
    <ShortAnswerInputMain
      className="do-test-exercise-short-answer-input font-inter"
      contentEditable={false}
      style={{ marginLeft: 8, display: store.isViewResult ? 'inline' : 'inline-block' }}
    >
      {isNotInsideSubject ? (
        '_____'
      ) : store.isViewResult ? (
        <Box display={store.isViewResult ? 'inline' : 'block'}>{renderResult()}</Box>
      ) : (
        <UserAnswerInput
          value={store.userAnswers.get(id)?.userAnswer ?? ''}
          onChange={(newValue) => {
            store.answer({ id, answer: newValue });
          }}
        />
      )}
      {children}
    </ShortAnswerInputMain>
  );
});

const UserAnswerInput = styled(SlateAutoSizeInput)`
  > * {
    border-bottom: 2px solid ${colorsV2.gray100};

    &:focus {
      border-bottom: 2px solid ${colorsV2.blue100};
    }
  }
`;
